<template>
    <div
        class="not-found-container relative overflow-hidden min-h-screen flex items-center justify-center bg-slate-50 select-none font-sans">

        <!-- پس‌زمینه گرادینت متحرک -->
        <div class="absolute inset-0 z-0 pointer-events-none">
            <div
                class="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] rounded-full opacity-20 blur-[80px] bg-gradient-to-r from-[#eb4763] to-[#a347d1] animate-pulse-slow">
            </div>
            <div
                class="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] rounded-full opacity-20 blur-[80px] bg-gradient-to-r from-[#2eb8aa] to-[#eb4763] animate-pulse-slow animation-delay-2000">
            </div>
            <div class="absolute top-1/4 left-1/4 w-2 h-2 bg-white/30 rounded-full animate-float"></div>
            <div
                class="absolute top-3/4 right-1/3 w-3 h-3 bg-[#a347d1]/30 rounded-full animate-float animation-delay-1000">
            </div>
            <div
                class="absolute top-1/2 left-2/3 w-2 h-2 bg-[#2eb8aa]/30 rounded-full animate-float animation-delay-500">
            </div>
        </div>

        <div class="relative z-10 container mx-auto px-4 text-center">

            <!-- عنوان 404 -->
            <h1 class="text-8xl sm:text-9xl font-black mb-4 tracking-tighter relative inline-block" style=" margin: 60px;">
                <span
                    class="bg-gradient-to-r from-[#eb4763] via-[#a347d1] to-[#2eb8aa] bg-clip-text text-transparent drop-shadow-[0_0_15px_rgba(235,71,99,0.5)] filter">
                    404
                </span>
                <div
                    class="absolute -inset-2 bg-gradient-to-r from-[#eb4763] via-[#a347d1] to-[#2eb8aa] blur-xl opacity-30 -z-10 animate-pulse">
                </div>
            </h1>

            <p class="text-lg sm:text-xl text-slate-600 mb-10 font-medium max-w-lg mx-auto leading-relaxed">
                این صفحه گم شده! <br>
                اما جای نگرانی نیست، یه دنیای جدید منتظرته.
            </p>

            <!-- محیط بازی -->
            <div ref="gameContainerRef"
                class="game-wrapper relative w-full max-w-4xl h-64 bg-white/30 backdrop-blur-xl rounded-3xl border border-white/60 shadow-[0_20px_50px_rgba(0,0,0,0.1)] overflow-hidden cursor-pointer mx-auto group hover:shadow-[0_20px_60px_rgba(235,71,99,0.2)] transition-all duration-500"
                @click="handleJump">
                <!-- خط افق نئونی -->
                <div
                    class="absolute bottom-0 w-full h-1.5 bg-gradient-to-r from-[#eb4763] via-[#a347d1] to-[#2eb8aa] shadow-[0_0_15px_rgba(235,71,99,0.6)]">
                </div>

                <!-- امتیاز -->
                <div class="absolute top-6 right-6 font-mono text-3xl font-bold text-slate-700 tracking-widest">
                    {{ Math.floor(score) }}
                </div>

                <!-- دایناسور -->
                <div class="dino absolute bottom-0 left-10 w-14 h-14 transition-transform duration-75 ease-linear z-20"
                    :style="{ transform: `translateY(-${dinoY}px)` }">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                        class="w-full h-full drop-shadow-[0_0_8px_rgba(235,71,99,0.8)]">
                        <path
                            d="M19 5H17V3H15V5H13V7H11V9H9V11H7V13H5V15H3V17H5V19H7V21H9V19H11V17H13V15H15V13H17V11H19V9H21V7H19V5Z"
                            fill="#eb4763" stroke="#fff" stroke-width="1.5" />
                        <circle cx="15" cy="4" r="2" fill="#fff" />
                        <path d="M16 8L18 9" stroke="#fff" stroke-width="1.5" stroke-linecap="round" />
                    </svg>
                </div>

                <!-- کاکتوس‌ها -->
                <div v-for="cactus in cacti" :key="cactus.id" class="cactus absolute bottom-0 z-10" :style="{
                    left: `${cactus.x}px`,
                    width: `${cactus.width}px`,
                    height: `${cactus.height}px`
                }">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
                        class="w-full h-full drop-shadow-[0_0_8px_rgba(46,184,170,0.8)]">
                        <path d="M12 2L14 8H10L12 2ZM10 8V22H14V8H10ZM8 10V22H6V10H8ZM16 10V22H18V10H16Z" fill="#2eb8aa"
                            stroke="#fff" stroke-width="1.5" />
                    </svg>
                </div>

                <!-- لایه شروع بازی -->
                <div v-if="!isPlaying && !isGameOver"
                    class="absolute inset-0 flex flex-col items-center justify-center bg-white/40 backdrop-blur-md z-30 transition-opacity duration-300">
                    <div class="animate-bounce-soft mb-4">
                        <span class="text-5xl block">🦖</span>
                    </div>

                    <button @click.stop="startGame"
                        class="group/btn relative px-8 py-4 rounded-2xl bg-gradient-to-r from-[#eb4763] to-[#a347d1] text-white font-bold text-lg shadow-lg hover:shadow-[0_0_20px_rgba(235,71,99,0.6)] hover:scale-105 active:scale-95 transition-all duration-300 overflow-hidden">
                        <span class="relative z-10 flex items-center gap-2">
                            <span>شروع بازی</span>
                            <ArrowRight :size="20"
                                class="group-hover/btn:translate-x-1 rtl:group-hover/btn:-translate-x-1 transition-transform" />
                        </span>
                        <div
                            class="absolute inset-0 bg-white/20 translate-y-full group-hover/btn:translate-y-0 transition-transform duration-300">
                        </div>
                    </button>

                    <p
                        class="mt-4 text-sm text-slate-600 font-medium bg-white/50 px-3 py-1 rounded-full backdrop-blur-sm">
                        یا کلید <kbd
                            class="font-mono bg-white px-2 py-0.5 rounded border border-slate-300 shadow-sm">Space</kbd>
                        را بزنید
                    </p>
                </div>

                <!-- لایه باخت -->
                <div v-if="isGameOver"
                    class="absolute inset-0 flex flex-col items-center justify-center bg-red-500/20 backdrop-blur-md z-30 animate-fade-in">
                    <h3 class="text-4xl font-black text-[#eb4763] mb-2 drop-shadow-lg">باختی!</h3>
                    <p class="text-slate-700 mb-6 font-medium">امتیاز شما: {{ Math.floor(score) }}</p>

                    <button @click.stop="resetGame"
                        class="px-8 py-3 rounded-xl bg-white text-[#eb4763] font-bold shadow-lg hover:bg-slate-50 hover:scale-105 active:scale-95 transition-all duration-300 flex items-center gap-2">
                        <RefreshCcw :size="18" />
                        تلاش دوباره
                    </button>
                </div>
            </div>

            <!-- دکمه بازگشت -->
            <router-link to="/"
            style="margin: 70px 0 50px 0;"
                class="mt-12 inline-flex items-center gap-2 px-8 py-4 rounded-2xl bg-white/80 backdrop-blur-sm border border-slate-200 text-slate-700 font-semibold hover:bg-white hover:border-[#eb4763] hover:text-[#eb4763] hover:shadow-lg transition-all duration-300 group">
                <ArrowRight :size="18"
                    class="group-hover:-translate-x-1 rtl:group-hover:translate-x-1 transition-transform" />
                بازگشت به خانه
            </router-link>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { ArrowRight, RefreshCcw } from 'lucide-vue-next';

const gameContainerRef = ref(null);

// وضعیت بازی
const isPlaying = ref(false);
const isGameOver = ref(false);
const score = ref(0);
const dinoY = ref(0);
const cacti = ref([]);

// متغیرهای فیزیک
let animationFrameId;
let lastTime = 0;
let spawnTimer = 0;
let gameSpeed = 7;
const GRAVITY = 0.9;
const JUMP_FORCE = -15;
const DINO_WIDTH = 56;
const DINO_X_POS = 40;
const CACTUS_MIN_WIDTH = 20;
const CACTUS_MAX_WIDTH = 40;

// سرعت عمودی دایناسور (برای محاسبه پرش)
let velocity = 0;

// تابع پرش (فقط ورودی را دریافت می‌کند)
const handleJump = () => {
    // اگر بازی تمام شده یا هنوز شروع نشده، کاری نکن (به جز شروع بازی توسط اسپیس در رویداد کلید)
    if (!isPlaying.value || isGameOver.value) return;

    // اگر دایناسور روی زمین است، فقط یک بار پرش را فعال کن
    if (dinoY.value === 0) {
        velocity = JUMP_FORCE;
    }
};

// حلقه اصلی بازی
const gameLoop = (timestamp) => {
    if (!isPlaying.value || isGameOver.value) return;

    const deltaTime = timestamp - lastTime;
    lastTime = timestamp;

    // --- فیزیک دایناسور ---
    // اعمال جاذبه به سرعت عمودی
    velocity += GRAVITY;

    // آپدیت موقعیت عمودی
    dinoY.value += velocity;

    // برخورد با زمین (جلوگیری از افتادن به زیر زمین)
    if (dinoY.value >= 0) {
        dinoY.value = 0;
        velocity = 0;
    }

    // --- حرکت کاکتوس‌ها ---
    cacti.value.forEach(cactus => {
        cactus.x -= gameSpeed * (deltaTime / 16);

        // حذف کاکتوس‌های خارج شده از صفحه
        if (cactus.x < -50) {
            cactus.x = gameContainerRef.value?.offsetWidth || window.innerWidth;
            cactus.width = Math.floor(Math.random() * (CACTUS_MAX_WIDTH - CACTUS_MIN_WIDTH + 1) + CACTUS_MIN_WIDTH);
            score.value++;
            // افزایش سرعت هر ۵ امتیاز
            if (score.value % 5 === 0) {
                gameSpeed += 0.5;
            }
        }

        // تشخیص برخورد
        const dinoLeft = DINO_X_POS;
        const dinoRight = DINO_X_POS + DINO_WIDTH;

        // بررسی برخورد افقی
        if (
            cactus.x < dinoRight &&
            cactus.x + cactus.width > dinoLeft
        ) {
            // بررسی برخورد عمودی: اگر ارتفاع دایناسور کمتر از ارتفاع کاکتوس باشد (یعنی دایناسور پایین‌تر از کاکتوس است)
            // توجه: dinoY معکوس است (مثبت یعنی بالا)، پس اگر dinoY کوچک باشد یعنی نزدیک زمین است.
            if (dinoY.value < cactus.height) {
                gameOver();
            }
        }
    });

    // تولید کاکتوس جدید
    spawnTimer += deltaTime;
    if (spawnTimer > 1500 + Math.random() * 1000) {
        cacti.value.push({
            id: Date.now(),
            x: gameContainerRef.value?.offsetWidth || window.innerWidth,
            width: Math.floor(Math.random() * (CACTUS_MAX_WIDTH - CACTUS_MIN_WIDTH + 1) + CACTUS_MIN_WIDTH),
            height: 40 + Math.random() * 20
        });
        spawnTimer = 0;
    }

    animationFrameId = requestAnimationFrame(gameLoop);
};

const startGame = () => {
    if (isGameOver.value) resetGame();

    isPlaying.value = true;
    isGameOver.value = false;
    score.value = 0;
    gameSpeed = 7;
    dinoY.value = 0;
    velocity = 0; // ریست کردن سرعت عمودی
    cacti.value = [];
    lastTime = performance.now();
    spawnTimer = 0;

    animationFrameId = requestAnimationFrame(gameLoop);
};

const gameOver = () => {
    isPlaying.value = false;
    isGameOver.value = true;
    cancelAnimationFrame(animationFrameId);
};

const resetGame = () => {
    isGameOver.value = false;
    score.value = 0;
    gameSpeed = 7;
    dinoY.value = 0;
    velocity = 0;
    cacti.value = [];
    startGame();
};

onMounted(() => {
    // مدیریت کلید Space
    window.addEventListener('keydown', (e) => {
        if (e.code === 'Space') {
            e.preventDefault(); // جلوگیری از اسکرول صفحه

            if (!isPlaying.value && !isGameOver.value) {
                startGame();
            } else if (isPlaying.value) {
                handleJump();
            }
        }
    });
});

onUnmounted(() => {
    cancelAnimationFrame(animationFrameId);
});
</script>

<style scoped>
@keyframes pulse-slow {

    0%,
    100% {
        opacity: 0.1;
        transform: scale(1);
    }

    50% {
        opacity: 0.2;
        transform: scale(1.1);
    }
}

@keyframes bounce-soft {

    0%,
    100% {
        transform: translateY(0);
    }

    50% {
        transform: translateY(-10px);
    }
}

@keyframes float {

    0%,
    100% {
        transform: translateY(0);
    }

    50% {
        transform: translateY(-15px);
    }
}

@keyframes fade-in {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

.animate-pulse-slow {
    animation: pulse-slow 4s ease-in-out infinite;
}

.animation-delay-2000 {
    animation-delay: 2s;
}

.animation-delay-1000 {
    animation-delay: 1s;
}

.animation-delay-500 {
    animation-delay: 0.5s;
}

.animate-bounce-soft {
    animation: bounce-soft 2s ease-in-out infinite;
}

.animate-float {
    animation: float 3s ease-in-out infinite;
}

.animate-fade-in {
    animation: fade-in 0.3s ease-out forwards;
}

@media (max-width: 640px) {
    .dino {
        width: 3rem;
        height: 3rem;
    }

    .game-wrapper {
        height: 56px;
    }
}
</style>